import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { useModal } from '@/contexts/modal'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import {
    NEGOTIATION_PRIORITY,
    NEGOTIATION_STAGES,
    NEGOTIATION_TYPES,
} from '@/modules/Customer/constants'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { formatPGDate, renderFileItem } from '@/utils/function'
import { useState } from 'react'
import { Control, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import createNegotiationValidate from './schema'
import dayjs from 'dayjs'

export interface CreateNegotiationProps extends ModalProps {
    customerJourneyId: number
    customerId: number
    customerJourneyStep?: string
    onSubmit: (data: any) => Promise<void>
}

export interface ContractNegotiationForm {
    contract_negotiation_title: string
    contract_negotiation_type: string
    contract_negotiation_content: string
    contract_negotiation_status: string
    contract_negotiation_priority: string
    contract_negotiation_deadline?: string
    contract_negotiation_assignees: number[]
    contract_negotiation_customer_id: number
    contract_negotiation_stage: string
    contract_negotiation_notes?: string
    contract_negotiation_value: number
    contract_negotiation_progress: number
    contract_negotiation_customer_journey_id: number
    contract_negotiation_customer_journey_step?: string | null
    attachments: File[]
}

const CreateNegotiation: React.FC<CreateNegotiationProps> = ({
    onClose,
    open,
    onSubmit,
    customerJourneyId,
    customerId,
    customerJourneyStep,
}) => {
    const { t } = useTranslation()
    const { closeModal } = useModal()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_NEGOTIATION,
    })

    const formControl = useForm<ContractNegotiationForm>({
        defaultValues: {
            contract_negotiation_title: '',
            contract_negotiation_type: 'quote_request',
            contract_negotiation_content: '',
            contract_negotiation_status: 'draft',
            contract_negotiation_priority: 'medium',
            contract_negotiation_deadline: dayjs()?.format('YYYY-MM-DD'),
            contract_negotiation_assignees:
                import.meta.env.APP_PROCESS_ASSIGNEE_IDS.split(',').map(
                    Number
                ) || [],
            contract_negotiation_stage: 'contract_preparation',
            contract_negotiation_notes: '',
            contract_negotiation_value: 0,
            contract_negotiation_progress: 0,
            contract_negotiation_customer_id: customerId,
            contract_negotiation_customer_journey_id: customerJourneyId,
            contract_negotiation_customer_journey_step:
                customerJourneyStep ?? 'contract',
            attachments: [],
        },
        // @ts-ignore
        resolver: yupResolver(createNegotiationValidate),
    })

    const handleSubmit = async (values: ContractNegotiationForm) => {
        setIsSubmitting(true)
        try {
            let attachments: Attachment[] = []
            if (values.attachments && values.attachments.length > 0) {
                const attachmentUploadeds = await uploadAsync(
                    values.attachments
                )
                attachments = attachmentUploadeds
                    ?.filter((attachment) => attachment.status === 'fulfilled')
                    .map((attachment) => attachment.value) as Attachment[]
            }

            await onSubmit({
                ...values,
                contract_negotiation_deadline: formatPGDate(
                    values.contract_negotiation_deadline,
                    'YYYY-MM-DDTHH:mm:ssZ'
                ),
                attachments,
            })
            closeModal()
        } catch (error) {
            console.error('Error creating negotiation:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const onDelete = (file: File | Attachment) => {
        const newAttachments = formControl
            .getValues('attachments')
            .filter((f: File | Attachment) => f !== file)
        formControl.setValue('attachments', newAttachments)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('create-negotiation')}
            maxWidth="md"
        >
            <GridHookForm
                formControl={formControl}
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: closeModal,
                    },
                    {
                        title: t('create'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isSubmitting,
                        disabled: isSubmitting,
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'contract_negotiation_title',
                            type: 'textfield',
                            label: t('negotiations.title'),
                            placeholder: t('enter-negotiation-title'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_type',
                            type: 'select',
                            label: t('negotiations.type'),
                            placeholder: t('select-negotiation-type'),
                            options: NEGOTIATION_TYPES(t),
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_priority',
                            type: 'select',
                            label: t('negotiations.priority'),
                            placeholder: t('select-priority'),
                            options: NEGOTIATION_PRIORITY(t),
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_stage',
                            type: 'select',
                            label: t('negotiations.stage'),
                            placeholder: t('select-stage'),
                            options: NEGOTIATION_STAGES(t),
                            allowClear: false,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_value',
                            type: 'number',
                            label: t('negotiations.value'),
                            placeholder: t('enter-value'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_deadline',
                            type: 'datepicker',
                            label: t('negotiations.deadline'),
                            placeholder: t('select-deadline'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        className: 'col-span-4',
                        renderEditComponent: (
                            control: Control<ContractNegotiationForm>
                        ) => {
                            return (
                                <EmployeeSelect
                                    multiple
                                    showLabel
                                    control={control}
                                    name="contract_negotiation_assignees"
                                    label={t('negotiations.assignees')}
                                />
                            )
                        },
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_content',
                            type: 'textarea',
                            label: t('negotiations.content'),
                            placeholder: t('enter-negotiation-content'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'contract_negotiation_notes',
                            type: 'textarea',
                            label: t('negotiations.notes'),
                            placeholder: t('enter-notes'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            multiple: true,
                            label: t('attachments'),
                            placeholder: t('attachments'),
                            renderFileItem: (file: File) => {
                                return renderFileItem(file, onDelete)
                            },
                        },
                        className: 'col-span-full',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateNegotiation
